import {useMemo} from 'react';

import {ACTIONS_REGISTRY} from '../search-bar/command-palette/actions-registry';
import {NAVIGATION_REGISTRY} from '../search-bar/command-palette/navigation-registry';

type Mode = 'search' | 'commands';

/**
 * All completable strings — labels AND keywords from both registries.
 * Sorted shortest-first so we suggest the most concise completion.
 */
function buildCompletions(): string[] {
    const all: string[] = [];

    for (const item of NAVIGATION_REGISTRY) {
        all.push(item.label, ...item.keywords);
    }
    for (const item of ACTIONS_REGISTRY) {
        all.push(item.label, ...item.keywords);
    }

    const seen = new Set<string>();

    return all
        .filter((s) => s.length >= 4)
        .filter((s) => {
            const lower = s.toLowerCase();
            if (seen.has(lower)) return false;
            seen.add(lower);

            return true;
        })
        .sort((a, b) => a.length - b.length);
}

const ALL_COMPLETIONS = buildCompletions();

export function useGhostText(mode: Mode, searchValue: string): string | null {
    return useMemo(() => {
        if (mode !== 'commands') return null;

        const trimmed = searchValue.trim();
        if (trimmed.length < 2) return null;

        const lower = trimmed.toLowerCase();

        for (const completion of ALL_COMPLETIONS) {
            if (completion.toLowerCase().startsWith(lower) && completion.length > trimmed.length) {
                // Return typed text + remaining completion, preserving user casing
                return searchValue + completion.slice(trimmed.length);
            }
        }

        return null;
    }, [mode, searchValue]);
}
